import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Inbox, Upload, RefreshCcw, Loader2, FileText, Mail } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONES = {
  EXPECTED: "bg-amber-100 text-amber-900 border-amber-300",
  ARRIVED:  "bg-sky-100  text-sky-900   border-sky-300",
  PROCESSED:"bg-emerald-100 text-emerald-900 border-emerald-300",
  CANCELLED:"bg-gray-100 text-gray-500  border-gray-300",
};

export default function DocumentIntake() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [uploading, setUploading] = useState(false);
  // EXPECTED is the working list. ALL shows arrivals & archived items so the
  // operator can still find a previous announcement after the tank gates in.
  const [filter, setFilter] = useState("EXPECTED");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/document-intake?limit=500")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/document-intake/poll-now");
      const n = data?.processed ?? 0;
      toast.success(n ? `Polled — ${n} new messages processed` : "Polled — no new mail");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPolling(false); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/document-intake/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data?.row_count ?? 0} tanks (PO ${data?.po_number ?? "—"})`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/document-intake/${id}`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Group by PO/batch so each Stolt packet renders as one card.
  // Apply the status filter first so a batch shrinks/disappears when all its
  // tanks have arrived. ALL keeps everything.
  const visible = filter === "ALL"
    ? items
    : items.filter((r) => r.status === filter);

  const groups = visible.reduce((acc, row) => {
    const k = row.batch_id || row.po_number || row.id;
    (acc[k] ||= { meta: row, tanks: [] }).tanks.push(row);
    return acc;
  }, {});
  const ordered = Object.values(groups).sort(
    (a, b) => (b.meta.received_at || "").localeCompare(a.meta.received_at || "")
  );

  // Status counts for the filter pills
  const counts = items.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    acc.ALL = (acc.ALL || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="document-intake-page">
      <PageHeader
        title="Expected Arrivals"
        subtitle="Tanks announced by carriers ahead of gate-in. We auto-parse Stolt depot & inland instructions and pre-fill the Gate-IN form when the tank arrives."
        testid="intake-header"
        actions={
          <div className="flex gap-2">
            <label className="ps-btn ps-btn-secondary cursor-pointer" data-testid="intake-upload">
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Manual ZIP</>}
              <input type="file" accept=".zip,.pdf" className="hidden" onChange={onUpload} disabled={uploading} />
            </label>
            <button className="ps-btn ps-btn-secondary" onClick={pollNow} disabled={polling} data-testid="intake-poll-now">
              {polling ? <><Loader2 className="w-4 h-4 animate-spin" /> Polling…</> : <><RefreshCcw className="w-4 h-4" /> Poll now</>}
            </button>
          </div>
        }
      />
      <div className="p-6 sm:p-8 space-y-4">
        <div className="border border-gray-200 bg-[#FAFAF7] p-4 flex items-start gap-3 text-xs text-gray-700">
          <Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#006E9E]" />
          <div>
            Forward Stolt depot or inland instructions to <span className="font-mono font-bold">inbound-depot-software@portside.nl</span>.
            We auto-parse PO #, tank numbers, previous cargo and EFTCO codes. Each announced tank shows up below as <b>EXPECTED</b>; the moment it gates in, it auto-flips to <b>ARRIVED</b> and disappears from the working list (still searchable under "All").
          </div>
        </div>

        {/* Status filter pills */}
        <div className="flex flex-wrap gap-2" data-testid="intake-filter">
          {[
            { k: "EXPECTED",  l: "Expected" },
            { k: "ARRIVED",   l: "Arrived" },
            { k: "PROCESSED", l: "Processed" },
            { k: "CANCELLED", l: "Cancelled" },
            { k: "ALL",       l: "All" },
          ].map(({ k, l }) => {
            const n = counts[k] || 0;
            const active = filter === k;
            return (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 border transition ${
                  active
                    ? "bg-[#002FA7] border-[#002FA7] text-white"
                    : "bg-white border-gray-300 text-gray-700 hover:border-gray-500"
                }`}
                data-testid={`intake-filter-${k.toLowerCase()}`}
              >
                {l} <span className="font-mono ml-1.5 opacity-80">{n}</span>
              </button>
            );
          })}
        </div>

        {loading && <div className="text-center text-gray-400 py-10">Loading…</div>}
        {!loading && ordered.length === 0 && (
          <div className="border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500">
            <Inbox className="w-8 h-8 mx-auto mb-3 text-gray-300" />
            {filter === "EXPECTED"
              ? "No expected arrivals — all announced tanks have already gated in. Forward a Stolt depot or inland instruction to the mailbox above, or drop a ZIP via the Manual ZIP button."
              : filter === "ALL"
              ? "No documents yet. Forward a Stolt depot or inland instruction to the mailbox above, or drop a ZIP via the Manual ZIP button."
              : `No tanks with status ${filter} right now.`}
          </div>
        )}

        {!loading && ordered.map((g) => {
          const m = g.meta;
          return (
            <div key={m.batch_id || m.id} className="border border-gray-200 bg-white" data-testid={`intake-batch-${m.batch_id}`}>
              <div className="flex items-baseline gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50">
                <FileText className="w-4 h-4 text-[#006E9E] shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-bold flex items-center gap-2">
                    PO {m.po_number || "—"} · {m.product || "Unknown product"}
                    {m.doc_type === "INLAND_INSTRUCTION" && (
                      <span className="text-[9px] uppercase tracking-[0.16em] font-bold px-1.5 py-0.5 border border-amber-400 text-amber-800 bg-amber-50 rounded-sm" data-testid="badge-inland">
                        Inland · empty-unclean
                      </span>
                    )}
                    {m.origin_city && (
                      <span className="text-[10px] font-mono uppercase text-gray-500">from {m.origin_city}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {m.sender} · received {new Date(m.received_at).toLocaleString()} · {g.tanks.length} tank{g.tanks.length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400 font-mono">{m.source}</div>
              </div>
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Tank</th>
                    <th>Previous cargo</th>
                    <th>EFTCO codes</th>
                    <th>Tests on file</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {g.tanks.map((t) => (
                    <tr key={t.id}>
                      <td className="font-mono font-bold">{t.tank_number}</td>
                      <td className="font-mono text-xs">{t.previous_cargo || "—"}</td>
                      <td className="font-mono text-[10px] text-gray-600">{(t.eftco_codes || []).join(" ") || "—"}</td>
                      <td className="text-[10px] text-gray-600">{(t.test_certs || []).map(tc => tc.kind).join(", ") || "—"}</td>
                      <td>
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 border rounded-sm ${STATUS_TONES[t.status] || ""}`}>
                          {t.status}
                        </span>
                      </td>
                      <td className="text-right">
                        {t.status !== "PROCESSED" && (
                          <button onClick={() => setStatus(t.id, "PROCESSED")} className="text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-700 hover:underline">Mark processed</button>
                        )}
                        {t.status === "EXPECTED" && (
                          <button onClick={() => setStatus(t.id, "CANCELLED")} className="text-[10px] uppercase tracking-[0.14em] font-bold text-red-700 hover:underline ml-3">Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}
